import { useQuery } from '@tanstack/react-query';
import { Fingerprint, Mail } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestClient } from '../../api/RestClientContext';
import { IdentityInfo } from '../../models/account';
import FullScreenError from '../../shared/components/FullScreenError';
import FullScreenLoader from '../../shared/components/FullScreenLoader';
import LogoBar from '../../shared/components/LogoBar';
import './AccountScreen.css';

interface AccountScreenProps {
    onLogout?: () => void;
}

export const AccountScreen: React.FC<AccountScreenProps> = ({ onLogout }) => {
    const client = useRestClient();
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['account-info'],
        queryFn: client.getAccountInfo,
        retry: false,
    });

    if (isLoading) return <FullScreenLoader />;
    if (error || !data?.success || !data.result) return <FullScreenError onRetry={refetch} />;

    const account = data.result;
    const identityLabel = (identity: IdentityInfo) => identity.type === 'EMAIL' ? 'Email' : identity.type;

    return (
        <main className="account-screen">
            <LogoBar />
            <section className="account-panel glass-ui">
                <span className="email-auth-kicker">ACCOUNT</span>
                <h1>Ваш аккаунт</h1>
                <p className="account-intro">Управляйте подключёнными способами входа и данными профиля.</p>
                <div className="account-profile-summary">
                    <div className="account-profile-primary">
                        <span className="account-profile-label">Telegram</span>
                        <strong>{account.tgName ? `@${account.tgName.replace(/^@/, '')}` : 'Пользователь Telegram'}</strong>
                    </div>
                    <div className="account-profile-id-block">
                        <span className="account-profile-label">ID</span>
                        <strong>{account.tgId}</strong>
                    </div>
                </div>
                <div className="account-section-heading">
                    <span className="account-profile-label">CONNECTED IDENTITIES</span>
                    <span className="account-identity-count">{account.identities.length}</span>
                </div>
                <div className="account-profile-list">
                    {account.identities.length > 0 ? account.identities.map((identity) => (
                        <div className="account-profile-row account-profile-row-bound" key={`${identity.type}:${identity.identity}`}>
                            <span className="account-identity-icon"><Mail size={19} aria-hidden="true" /></span>
                            <div>
                                <span className="account-profile-label">{identityLabel(identity)}</span>
                                <strong>{identity.identity}</strong>
                            </div>
                        </div>
                    )) : (
                        <div className="account-empty-state">
                            <Fingerprint size={21} aria-hidden="true" />
                            <span>Пока нет привязанных identities</span>
                        </div>
                    )}
                </div>
                <button className="account-bind-button" type="button" onClick={() => navigate('/account/bind/email')}>
                    <span>@ привязать email</span>
                </button>
                {localStorage.getItem('ogo.jwt') && (
                    <button className="email-auth-link" type="button" onClick={() => { localStorage.removeItem('ogo.jwt'); onLogout?.(); }}>
                        Выйти из web-сессии
                    </button>
                )}
            </section>
        </main>
    );
};
